import EditScriptLayout from "./NewScriptLayout";
import { Alert, Box, Button } from "@chakra-ui/react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { PROTECTED_PATHS } from "routes/pagePath";
import { errorToast, successToast } from "utils/hooks";
import axiosInstance from "services/httpService";
import { scriptsRequest } from "services/request";
import { convertParamsToString } from "helpers/stringManipulation";
import { QUERY_KEYS } from "utils/constants";
import { getRequestError } from "utils/requestError";
import CustomSpinner from "components/CustomSpinner";

const normalizeIds = (ids: any[] = []): string[] =>
  ids.flat().filter((id): id is string => typeof id === "string" && Boolean(id.trim()));

const EditScript = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const identity = searchParams.get("edit")?.trim() ?? "";
  const queryClient = useQueryClient();
  const [script, setScript] = useState<any>(null);
  const [loadedIdentity, setLoadedIdentity] = useState("");
  const [formValues, setFormValues] = useState<any>(null);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);

  useEffect(() => {
    if (!identity) return;
    let active = true;
    setLoadedIdentity("");
    setLoadError("");
    setSaveError("");
    const load = async () => {
      try {
        const url = convertParamsToString(scriptsRequest.SCRIPT, {
          id: encodeURIComponent(identity),
        });
        const response = await axiosInstance.get(url);
        if (!active) return;
        const detail = response.data?.data;
        if (!detail?.id || !detail?.slug || (detail.id !== identity && detail.slug !== identity)) {
          navigate(PROTECTED_PATHS.UPLOAD_ADMIN, { replace: true });
          return;
        }
        setScript(detail);
        setFormValues({
          ...detail,
          composer: detail.composer ?? "",
          videoUrl: detail.videoUrl ?? "",
          description: detail.description ?? "",
          lyrics: typeof detail.meta?.lyrics === "string" ? detail.meta.lyrics : "",
          language: detail.language ?? "",
          languageIds: (detail.languages ?? []).map((language) => language.id),
          usesOtherLanguage: false,
          category: detail.category ?? "All",
          categoryIds: (detail.categories ?? []).map((category) => category.id),
          customCategory: detail.customCategory ?? "",
          usesOtherCategory: detail.category === "others",
          documentName: "",
          audioName: "",
        });
        setLoadedIdentity(identity);
      } catch (error: any) {
        if (!active) return;
        if (error?.response?.status === 404 || error?.response?.status === 400) {
          navigate(PROTECTED_PATHS.UPLOAD_ADMIN, { replace: true });
        } else {
          setLoadError(getRequestError(error, "Unable to load this script. Please try again."));
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [identity, navigate, attempt]);

  const onChange = ({ target: { name, value } }) => {
    setFormValues((current) => ({ ...current, [name]: value }));
  };
  const onLanguageChange = ({ languageIds, language, usesOtherLanguage }) => {
    setFormValues((current) => ({
      ...current,
      language,
      languageIds: normalizeIds(languageIds),
      usesOtherLanguage,
    }));
  };
  const onCategoryChange = ({ categoryIds, category, customCategory, usesOtherCategory }) => {
    setFormValues((current) => ({
      ...current,
      category,
      customCategory,
      categoryIds: normalizeIds(categoryIds),
      usesOtherCategory,
    }));
  };
  const handleFileUpload = ({ target: { name, files } }) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      errorToast("File must be less than 10Mb");
      return;
    }
    if (name !== "audio" && !file.type.startsWith("image/") && file.type !== "application/pdf") {
      errorToast("Please select an image or PDF");
      return;
    }
    setFormValues((current) => ({ ...current, [name]: file, [`${name}Name`]: file.name }));
  };

  const handleSubmit = async () => {
    if (saving.current || loadedIdentity !== identity || !script?.id || !formValues) return;
    if (!formValues.name?.trim()) {
      errorToast("Please fill in a name");
      return;
    }
    if (!formValues.description?.trim()) {
      errorToast("Please enter a description before publication");
      return;
    }
    if (!formValues.language?.trim() && formValues.languageIds.length === 0) {
      errorToast("Please select a language");
      return;
    }
    if (formValues.categoryIds.length === 0) {
      errorToast("Please select a category");
      return;
    }
    if (formValues.category === "others" && !formValues.customCategory.trim()) {
      errorToast("Please enter a custom category");
      return;
    }

    saving.current = true;
    setIsSaving(true);
    setSaveError("");
    let metadataSaved = false;
    try {
      const {
        name,
        composer,
        composerId,
        category,
        description,
        videoUrl,
        language,
        customCategory,
        lyrics,
      } = formValues;
      const url = convertParamsToString(scriptsRequest.EDIT_SCRIPT, { id: script.id });
      await axiosInstance.patch(url, {
        name,
        composer,
        composerId,
        category,
        description,
        videoUrl,
        language,
        customCategory,
        languageIds: normalizeIds(formValues.languageIds),
        categoryIds: normalizeIds(formValues.categoryIds),
        meta: JSON.stringify({ ...(script.meta ?? {}), lyrics }),
      });
      metadataSaved = true;
      if (formValues.audio instanceof File || formValues.document instanceof File) {
        const media = new FormData();
        if (formValues.audio instanceof File) media.append("audio", formValues.audio);
        if (formValues.document instanceof File) media.append("document", formValues.document);
        await axiosInstance.patch(
          convertParamsToString(scriptsRequest.UPDATE_SCRIPT_MEDIA, {
            slug: encodeURIComponent(script.slug),
          }),
          media,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      }
      void queryClient.invalidateQueries(QUERY_KEYS.ADMIN_SCRIPTS);
      successToast("Script updated. Review it before publication.");
      navigate(`/scripts/${encodeURIComponent(script.slug)}`, { replace: true });
    } catch (error) {
      const message = getRequestError(error, "Unable to save this script. Please try again.");
      setSaveError(
        metadataSaved
          ? `Metadata saved, but replacement media could not be saved. ${message} Retry to finish saving.`
          : message,
      );
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  if (!identity) return <Navigate to={PROTECTED_PATHS.UPLOAD_ADMIN} replace />;
  if (loadError)
    return (
      <Box p={4}>
        <Alert status="error">{loadError}</Alert>
        <Button
          onClick={() => {
            setAttempt((value) => value + 1);
          }}
        >
          Retry
        </Button>
        <Button
          onClick={() => {
            navigate(PROTECTED_PATHS.UPLOAD_ADMIN);
          }}
        >
          Back to verification
        </Button>
      </Box>
    );
  if (loadedIdentity !== identity || !formValues) return <CustomSpinner />;

  return (
    <>
      {saveError && <Alert status="error">{saveError}</Alert>}
      <EditScriptLayout
        formData={formValues}
        onChange={onChange}
        handleFileUpload={handleFileUpload}
        handleSubmit={() => {
          void handleSubmit();
        }}
        isLoading={isSaving}
        formValues={formValues}
        onLanguageChange={onLanguageChange}
        onCategoryChange={onCategoryChange}
        onCancel={() => {
          navigate(`/scripts/${encodeURIComponent(script.slug)}`);
        }}
      />
    </>
  );
};

export default EditScript;
